/**
 * CARLA attachment acceptance policy.
 *
 * Pure helpers so the picker logic is testable without rendering the chat
 * pane. `CarlaChatPane.handleFilePick` composes these to validate a file
 * before reading its bytes.
 */

export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const

export const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// Sprint 19 hot-fix (2026-05-05): PDF attachments. CARLA reads them as
// Claude `document` content blocks — single + bulk supported.
export const PDF_MEDIA_TYPE = 'application/pdf'

export const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

export const CSV_MEDIA_TYPE = 'text/csv'
export const TEXT_MEDIA_TYPE = 'text/plain'
export const MARKDOWN_MEDIA_TYPE = 'text/markdown'

export const ACCEPTED_FILE_TYPES = [
  ...ACCEPTED_IMAGE_TYPES,
  XLSX_MEDIA_TYPE,
  PDF_MEDIA_TYPE,
  DOCX_MEDIA_TYPE,
  CSV_MEDIA_TYPE,
  TEXT_MEDIA_TYPE,
  MARKDOWN_MEDIA_TYPE,
]

// 15MB unified cap — large enough for real vendor quote workbooks while
// keeping the chat request payload reasonable.
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024

export type AttachmentValidation =
  | { ok: true; effectiveType: string }
  | { ok: false; reason: 'unsupported-type' | 'too-large'; message: string }

/**
 * Validate a File against CARLA's acceptance policy. Returns either a
 * normalized `effectiveType` (falling back to extension-sniffing when
 * the browser reports an empty `file.type`, which happens on some OSes
 * for xlsx) or a reason + user-facing message.
 */
export function validateAttachment(file: File): AttachmentValidation {
  const name = file.name.toLowerCase()
  const effectiveType =
    file.type ||
    (name.endsWith('.xlsx') ? XLSX_MEDIA_TYPE :
     name.endsWith('.docx') ? DOCX_MEDIA_TYPE :
     name.endsWith('.csv')  ? CSV_MEDIA_TYPE  :
     name.endsWith('.txt')  ? TEXT_MEDIA_TYPE :
     name.endsWith('.md')   ? MARKDOWN_MEDIA_TYPE : '')

  if (!ACCEPTED_FILE_TYPES.includes(effectiveType as typeof ACCEPTED_FILE_TYPES[number])) {
    return {
      ok: false,
      reason: 'unsupported-type',
      message:
        'Supported formats: images (PNG, JPEG, GIF, WebP), .pdf, Word (.docx), ' +
        'Excel (.xlsx), .csv, and plain text (.txt, .md).\n\n' +
        'Legacy .xls and .doc files should be re-saved in the modern format first.',
    }
  }

  if (file.size > MAX_ATTACHMENT_BYTES) {
    return {
      ok: false,
      reason: 'too-large',
      message: `Attachment exceeds the 15 MB limit (got ${formatBytes(file.size)}).`,
    }
  }

  return { ok: true, effectiveType }
}

export function isImageMediaType(mediaType: string): boolean {
  return mediaType.startsWith('image/')
}

export function isPdfMediaType(mediaType: string): boolean {
  return mediaType === PDF_MEDIA_TYPE
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Extract image File entries from a paste event's `clipboardData.items`.
 * Browsers expose pasted screenshots as DataTransferItems with kind='file'
 * and an image/* MIME — non-image items (text/html, RTF) are ignored so
 * plain-text pastes still go through the textarea normally.
 *
 * Returns an array (possibly empty). Caller decides whether to
 * preventDefault on the paste event based on `result.length > 0`.
 */
export function extractImagesFromClipboard(
  items: DataTransferItemList | null | undefined,
): File[] {
  if (!items) return []
  const out: File[] = []
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (!item || item.kind !== 'file') continue
    if (!item.type || !item.type.startsWith('image/')) continue
    const file = item.getAsFile()
    if (file) out.push(file)
  }
  return out
}

/**
 * Browsers default clipboard image filenames to "image.png" — every paste
 * looks identical in the chip list. Rename to a timestamped form so the
 * user can tell pastes apart at a glance and so multiple pastes in one
 * message don't share a name.
 *
 * Output: `pasted-{ISO-no-colons}.{ext}`. ISO-with-colons would clash
 * with macOS path conventions on download; the colons-stripped form
 * keeps it portable.
 */
/**
 * Synthesize a short user-visible message when attachments are sent
 * without typed text. The middleware's ChatRequest schema requires
 * `message` to be non-empty (Pydantic `min_length=1`); a blank submit
 * with attachments would otherwise 422. Surfacing filenames also gives
 * Claude useful context for the attached blocks rather than an
 * unexplained binary payload.
 */
export function buildAttachmentPlaceholder(
  filenames: ReadonlyArray<string | undefined>,
): string {
  const named = filenames.filter((n): n is string => Boolean(n))
  return named.length > 0 ? `Attached: ${named.join(', ')}` : 'Attached files'
}

export function renamePastedImage(file: File, now: Date = new Date()): File {
  const ext =
    file.type === 'image/jpeg'
      ? 'jpg'
      : file.type === 'image/png'
        ? 'png'
        : file.type === 'image/gif'
          ? 'gif'
          : file.type === 'image/webp'
            ? 'webp'
            : 'png'

  const stamp = now.toISOString().replace(/[:.]/g, '-').replace('Z', '')
  const newName = `pasted-${stamp}.${ext}`
  return new File([file], newName, { type: file.type, lastModified: file.lastModified })
}
